import React, { useEffect, useMemo, useState } from 'react';
import { Translate } from 'src/translations/i18n';
import dayjs from 'dayjs';
import { Button, TextField, Grid, makeStyles, useTheme } from '@material-ui/core';
import Utils from 'src/utils';
// @ts-ignore
import { DateRange } from 'react-date-range-dayjs';
import 'react-date-range-dayjs/dist/styles.css';
import 'react-date-range-dayjs/dist/theme/default.css';
import { useSelector } from 'react-redux';
import { selectLocale } from 'src/store/selectors/translationSelectors';
import DatePickerPeriods from './DatePickerPeriods';
const useStyles = makeStyles((theme) => ({
    wrapper: {
        padding: theme.spacing(1)
    },
    dateInput: {
        marginLeft: theme.spacing(1)
    },
    calendar: {
        backgroundColor: 'transparent !important',
        '& .rdrMonth': {
            width: 250
        },
        '& .rdrDay': {
            height: 30
        },
        '& .rdrMonthName': {
            padding: '0 0 0 5px'
        },
        '& .rdrDayNumber span': {
            color: theme.palette.text.primary
        },
        '& .rdrMonthAndYearPickers select': {
            color: theme.palette.text.primary,
            backgroundColor: 'rgba(0,0,0,0.1)',
            padding: '5px 30px 5px 10px'
        },
        '& .rdrDayDisabled': {
            backgroundColor: 'rgba(0,0,0,0.1)'
        },
        '& .rdrNextPrevButton': {
            backgroundColor: theme.palette.primary.main
        },
        '& .rdrDateDisplayWrapper': {
            display: 'none'
        }
    },
    button: {
        margin: `${theme.spacing(1)}px 0 ${theme.spacing(1)}px ${theme.spacing(1)}px`
    }
}));
const disableFuture = (date) => dayjs(date, Utils.periods.shortFormat).isAfter(dayjs(), 'day');
function DateRangePicker({ value: { startDate: startDateProp, endDate: endDateProp }, disabledDate = disableFuture, onSubmit = (values) => console.log(values), onClose = () => null, periods = null, format = Utils.periods.shortFormat, showPresets = true, allowEmpty }) {
    const classes = useStyles();
    const theme = useTheme();
    const [startDate, setStartDate] = useState(startDateProp ? dayjs(startDateProp).utc(true) : startDateProp);
    const [endDate, setEndDate] = useState(endDateProp ? dayjs(endDateProp).utc(true) : endDateProp);
    const [inputStart, setInputStart] = useState(startDate ? dayjs(startDate, format).format(format) : startDate);
    const [inputEnd, setInputEnd] = useState(endDate ? dayjs(endDate, format).format(format) : endDate);
    useEffect(() => {
        setStartDate(startDateProp ? dayjs(startDateProp).utc(true) : startDateProp);
        setEndDate(endDateProp ? dayjs(endDateProp).utc(true) : endDateProp);
        setInputStart(startDateProp ? dayjs(startDateProp).format(format) : startDateProp);
        setInputEnd(endDateProp ? dayjs(endDateProp).format(format) : endDateProp);
    }, [startDateProp, endDateProp, format]);
    const lang = useSelector(selectLocale);
    const handleSelect = ([first, second]) => {
        const startDt = first.isBefore(second) ? first : second;
        const endDt = first.isBefore(second) ? second : first;
        const start = startDt.utc(true).startOf('day');
        const end = endDt.utc(true).endOf('day');
        setStartDate(start);
        setEndDate(end);
        setInputStart(start.format(format));
        setInputEnd(end.format(format));
    };
    const handleInput = (event) => {
        const { target: { value } } = event;
        if (allowEmpty && value === '') {
            return {
                value,
                date: null,
                isValid: true
            };
        }
        const date = dayjs(value, format).utc(true);
        return {
            value,
            date,
            isValid: date.isValid()
        };
    };
    const handleStartInput = (event) => {
        const { value, date, isValid } = handleInput(event);
        const stateDiff = !isValid
            ? {
                inputStart: value
            } : {
            inputStart: value,
            startDate: date
        };
        setStartDate(stateDiff.startDate?.startOf('day') || dayjs().utc(true));
        setInputStart(stateDiff.inputStart);
    };
    const handleEndInput = (event) => {
        const { value, date, isValid } = handleInput(event);
        const stateDiff = !isValid
            ? {
                inputEnd: value
            } : {
            inputEnd: value,
            endDate: date
        };
        setEndDate(stateDiff.endDate?.endOf('day') || dayjs().utc(true));
        setInputEnd(stateDiff.inputEnd);
    };
    const handleSubmit = () => {
        const start = inputStart ? startDate : null;
        const end = inputEnd ? endDate : null;
        onSubmit({
            startDate: start,
            endDate: end,
            inputStart,
            inputEnd
        });
    };
    const isValid = () => {
        const startValid = allowEmpty
            ? !inputStart || dayjs(inputStart, format).isValid()
            : dayjs(inputStart, format).isValid();
        const endValid = allowEmpty
            ? !inputEnd || dayjs(inputEnd, format).isValid()
            : dayjs(inputEnd, format).isValid();
        const isStartBeforeEnd = startDate && endDate
            ? startDate.isBefore(endDate, 'day') || startDate.isSame(endDate, 'day')
            : true;
        const validityObject = {
            startValid,
            endValid,
            isStartBeforeEnd
        };
        const valid = Object.values(validityObject).reduce((acc, item) => acc && item);
        return {
            ...validityObject,
            valid
        };
    };
    const { startValid, endValid, isStartBeforeEnd, valid } = isValid();
    const displayedRange = useMemo(() => ({
        startDate: startDate || dayjs().utc(true),
        endDate: endDate || dayjs().utc(true),
        key: 'selection'
    }), [startDate, endDate]);
    return (React.createElement(Grid, { container: true, direction: "column", className: classes.wrapper },
        React.createElement(Grid, { container: true, direction: "row" },
            showPresets && (React.createElement(Grid, { item: true },
                React.createElement(DatePickerPeriods, { selectedPeriod: [startDate, endDate], onChange: handleSelect, periods: periods }))),
            React.createElement(Grid, { item: true, style: { paddingRight: 20 } },
                React.createElement(Grid, { container: true, direction: "row", spacing: 1 },
                    React.createElement(Grid, { item: true, xs: 12, sm: 6 },
                        React.createElement(TextField, { value: inputStart, onChange: handleStartInput, error: !startValid || !isStartBeforeEnd, placeholder: !allowEmpty ? dayjs(startDate, format).format(format) : '', className: classes.dateInput, fullWidth: true })),
                    React.createElement(Grid, { item: true, xs: 12, sm: 6 },
                        React.createElement(TextField, { value: inputEnd, onChange: handleEndInput, placeholder: !allowEmpty ? dayjs(endDate, format).format(format) : '', error: !endValid || !isStartBeforeEnd, className: classes.dateInput, fullWidth: true }))),
                React.createElement(Grid, { container: true },
                    React.createElement(Grid, { item: true },
                        React.createElement(DateRange, { onChange: ({ selection }) => handleSelect([selection.startDate, selection.endDate]), className: classes.calendar, months: 2, ranges: [displayedRange], direction: "horizontal", weekStartsOn: 1, locale: lang, disabledDay: disabledDate || (() => false), rangeColors: [theme.palette.primary.main], color: theme.palette.primary.main }))))),
        React.createElement(Grid, { container: true, direction: "row", justifyContent: "space-between", alignItems: "center" },
            React.createElement(Grid, { item: true, xs: 12, sm: showPresets ? 3 : 12, style: { display: 'inline-flex' } },
                React.createElement(Button, { variant: "contained", color: "secondary", className: classes.button, onClick: onClose },
                    React.createElement(Translate, { value: "Cancel" })),
                React.createElement(Button, { onClick: handleSubmit, variant: "contained", color: "primary", className: classes.button, disabled: !valid },
                    React.createElement(Translate, { value: "Ok" }))))));
}
export default DateRangePicker;
